import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { Form, Field } from 'formik';
import { noop, omit, get } from 'lodash';
import { Trans, withI18n } from 'lingui-react';
import { compose, setDisplayName } from 'recompose';

import {
  Checkbox,
  Dropdown,
  InputField,
  InputGroup,
  Text,
} from 'base-components';
import { Column, Row } from 'styled-components-reactive-grid';

import PhoneNumberInput from 'elements/PhoneNumberInput';
import withPatternValidationOnKeyUp from 'utils/withPatternValidationOnKeyUp';
import { useFocusReceiver } from 'setup/FocusProvider';
import { invalidCharsForExporting } from 'constants';

import Error from './Error';
import ContactCallButton from './ContactCallButton';
import ContactDeleteButton from './ContactDeleteButton';
import { getContactErrors } from '../utils';
import { contactFields, fieldIds } from './constants';

export function renderContactTypes(contactTypes) {
  return contactTypes.map(contactType => (
    <Dropdown.ListItem
      key={contactType.option}
      id={contactType.option}
      type="button"
    >
      <Text name="contactType" value={contactType.option}>
        {contactType.text}
      </Text>
    </Dropdown.ListItem>
  ));
}

export function selectContactType(selected, contactTypes) {
  const selectedType = contactTypes.find(type => selected === type.option);

  return selectedType ? selectedType.text : '';
}

export class ContactDetailsForm extends Component {
  static propTypes = {
    contactTypes: PropTypes.arrayOf(
      PropTypes.shape({
        default: PropTypes.bool,
        option: PropTypes.string,
        text: PropTypes.string,
      }),
    ).isRequired,
    deletable: PropTypes.bool.isRequired,
    errors: PropTypes.shape({
      [contactFields.phone]: PropTypes.string,
      [contactFields.email]: PropTypes.string,
    }).isRequired,
    formId: PropTypes.string.isRequired,
    handleChange: PropTypes.func.isRequired,
    handleSubmit: PropTypes.func.isRequired,
    i18n: PropTypes.shape({
      _: PropTypes.func.isRequired,
      t: PropTypes.func.isRequired,
    }).isRequired,
    isReadOnlyCase: PropTypes.bool.isRequired,
    onDelete: PropTypes.func,
    setFieldValue: PropTypes.func.isRequired,
    setFieldTouched: PropTypes.func.isRequired,
    touched: PropTypes.shape({
      [contactFields.phone]: PropTypes.bool,
      [contactFields.email]: PropTypes.bool,
    }).isRequired,
    values: PropTypes.shape({
      [contactFields.callbackEta]: PropTypes.bool,
      [contactFields.callbackRollTime]: PropTypes.bool,
      [contactFields.contactType]: PropTypes.string,
      [contactFields.email]: PropTypes.string,
      [contactFields.name]: PropTypes.string,
      [contactFields.phone]: PropTypes.string,
      [contactFields.phoneExt]: PropTypes.string,
    }).isRequired,

    // from withPatternValidationOnKeyUp
    hasInvalidPattern: PropTypes.func,
    onValidatingFieldBlur: PropTypes.func,
    onValidatingFieldKeyUp: PropTypes.func,
    showValidationError: PropTypes.func,

    nameFocusHandler: PropTypes.func.isRequired,
    phoneFocusHandler: PropTypes.func.isRequired,
    emailFocusHandler: PropTypes.func.isRequired,
  };

  static defaultProps = {
    onDelete: noop,
    hasInvalidPattern: noop,
    onValidatingFieldBlur: noop,
    onValidatingFieldKeyUp: noop,
    showValidationError: noop,
  };

  constructor(props) {
    super(props);

    this.state = {
      contactName: get(props.values, contactFields.name, ''),
    };
  }

  onContactTypeChange = (event, itemId) => {
    event.preventDefault();
    if (itemId) {
      this.props.handleChange(contactFields.contactType)(itemId);
      this.props.handleSubmit(this.props.values);
    }
  };

  onEtaChange = event => {
    this.props.handleChange(contactFields.callbackEta)(event);
    this.props.handleSubmit(this.props.values);
  };

  onRollbackChange = event => {
    this.props.handleChange(contactFields.callbackRollTime)(event);
    this.props.handleSubmit(this.props.values);
  };

  handleResetNameClick = event => {
    event.stopPropagation();
    this.setState({ contactName: '' });
    this.props.handleChange(contactFields.name)('');
    this.props.handleSubmit(this.props.values);
  };

  handleNameChange = ({ target: { value } }, showValidationError) => {
    const { handleChange, hasInvalidPattern } = this.props;

    this.setState({ contactName: value });

    if (
      !value ||
      showValidationError ||
      !hasInvalidPattern(contactFields.name, value)
    ) {
      handleChange(contactFields.name)(value);
    }
  };

  handleNameBlur = event => {
    const { onValidatingFieldBlur, setFieldTouched } = this.props;
    onValidatingFieldBlur(contactFields.name);
    this.handleNameChange(event, true);
    setFieldTouched(contactFields.name);
  };

  handleNameKeyUp = event => {
    const { onValidatingFieldKeyUp } = this.props;
    const { showValidationError } = onValidatingFieldKeyUp(
      contactFields.name,
      event,
    );

    if (showValidationError) {
      this.handleNameChange(event, true);
    }
  };

  bindFocusHandler = focusHandler => el => focusHandler(el);

  render() {
    const {
      contactTypes,
      deletable,
      errors: allErrors,
      formId,
      i18n,
      isReadOnlyCase,
      onDelete,
      setFieldValue,
      setFieldTouched,
      touched,
      values,
      showValidationError,
      hasInvalidPattern,
      nameFocusHandler,
      phoneFocusHandler,
      emailFocusHandler,
    } = this.props;

    const { contactName } = this.state;

    const isContactNameValid =
      touched[contactFields.name] &&
      !hasInvalidPattern(contactFields.name, contactName);

    const errors =
      showValidationError(contactFields.name) && !isContactNameValid
        ? getContactErrors({ ...values, [contactFields.name]: contactName })
        : omit(allErrors, [contactFields.name]);

    const phoneNumber = values[contactFields.phone];
    const enableCallButton = !!phoneNumber && !errors[contactFields.phone];
    const isPhoneNumberValid =
      touched[contactFields.phone] && !errors[contactFields.phone];

    return (
      <Form>
        <InputGroup>
          <InputGroup.Row>
            <InputGroup.Column modifiers={['col']}>
              <Field
                name={contactFields.name}
                render={({ field }) => (
                  <InputField
                    {...field}
                    maxLength={50}
                    placeholder={i18n.t`Enter name...`}
                    readOnly={isReadOnlyCase}
                    value={contactName}
                    isValid={
                      !showValidationError(contactFields.name) ||
                      isContactNameValid
                    }
                    onChange={this.handleNameChange}
                    onBlur={this.handleNameBlur}
                  >
                    <InputField.Avatar />
                    <Column modifiers={['col', 'padScale_0']}>
                      <Row>
                        <InputField.Label>
                          <Trans>Name</Trans>
                        </InputField.Label>
                      </Row>
                      <Row>
                        <InputField.TextField
                          autoComplete="off"
                          onKeyUp={this.handleNameKeyUp}
                          innerRef={this.bindFocusHandler(nameFocusHandler)}
                        />
                        {field.value && (
                          <InputField.ActionButton
                            icon="times"
                            type="button"
                            onClick={this.handleResetNameClick}
                            modifiers={['padScaleX_0', 'hoverDanger']}
                          />
                        )}
                      </Row>
                    </Column>
                  </InputField>
                )}
              />
            </InputGroup.Column>
          </InputGroup.Row>
          <InputGroup.Row>
            <InputGroup.Column modifiers={['col']}>
              <Dropdown
                fullWidth
                hideOnChange
                onChange={this.onContactTypeChange}
                readOnly={isReadOnlyCase}
              >
                {({ isVisible }) => (
                  <Fragment>
                    <Dropdown.Target style={{ zIndex: isVisible ? 2 : 'auto' }}>
                      <InputField
                        name={contactFields.contactType}
                        onChange={noop}
                        readOnly={isReadOnlyCase}
                        value={selectContactType(
                          values[contactFields.contactType],
                          contactTypes,
                        )}
                      >
                        <Column modifiers={['col', 'padScaleY_0']}>
                          <Row>
                            <InputField.Label>
                              <Trans>Contact Type</Trans>
                            </InputField.Label>
                          </Row>
                          <Row>
                            <InputField.TextField
                              autoComplete="off"
                              style={{
                                caretColor: 'transparent',
                                cursor: 'pointer',
                              }}
                            />
                            <InputField.ActionButton
                              icon={isVisible ? 'chevron-up' : 'chevron-down'}
                              modifiers={['hoverInfo']}
                              onClick={noop}
                              type="button"
                            />
                            <Dropdown.Content>
                              <Dropdown.List>
                                {renderContactTypes(contactTypes)}
                              </Dropdown.List>
                            </Dropdown.Content>
                          </Row>
                        </Column>
                      </InputField>
                    </Dropdown.Target>
                  </Fragment>
                )}
              </Dropdown>
            </InputGroup.Column>
          </InputGroup.Row>
          <InputGroup.Row>
            <InputGroup.Column
              modifiers={['col']}
              style={{
                zIndex: isPhoneNumberValid ? 'auto' : 1,
              }}
            >
              <Field
                name={contactFields.phone}
                render={({ field }) => (
                  <InputField
                    {...field}
                    readOnly={isReadOnlyCase}
                    isValid={isPhoneNumberValid}
                  >
                    <Column modifiers={['col', 'padScaleY_0']}>
                      <Row>
                        <InputField.Label>
                          <Trans>Phone Number</Trans>
                        </InputField.Label>
                      </Row>
                      <Row>
                        <PhoneNumberInput
                          onBlur={() => setFieldTouched(contactFields.phone)}
                          onChange={value => setFieldValue(field.name, value)}
                          placeholder={i18n.t`(###) ###-####`}
                          readOnly={isReadOnlyCase}
                          value={field.value}
                          innerRef={this.bindFocusHandler(phoneFocusHandler)}
                        />
                      </Row>
                    </Column>
                  </InputField>
                )}
              />
            </InputGroup.Column>
            <InputGroup.Column modifiers={['col']}>
              <Field
                name={contactFields.phoneExt}
                render={({ field }) => (
                  <InputField
                    {...field}
                    placeholder={i18n.t`###`}
                    readOnly={isReadOnlyCase}
                  >
                    <Column modifiers={['col', 'padScaleY_0']}>
                      <Row>
                        <InputField.Label>
                          <Trans>Extension (Optional)</Trans>
                        </InputField.Label>
                      </Row>
                      <Row>
                        <InputField.TextField />
                      </Row>
                    </Column>
                  </InputField>
                )}
              />
            </InputGroup.Column>
          </InputGroup.Row>
          <InputGroup.Row>
            <InputGroup.Column modifiers={['col']}>
              <Field
                name={contactFields.email}
                render={({ field }) => (
                  <InputField
                    {...field}
                    isValid={
                      touched[contactFields.email] &&
                      !errors[contactFields.email]
                    }
                    maxLength={255}
                    placeholder={i18n.t`Enter email address...`}
                    readOnly={isReadOnlyCase}
                  >
                    <Column modifiers={['col', 'padScaleY_0']}>
                      <Row>
                        <InputField.Label>
                          <Trans>Email (Optional)</Trans>
                        </InputField.Label>
                      </Row>
                      <Row>
                        <InputField.TextField
                          innerRef={this.bindFocusHandler(emailFocusHandler)}
                        />
                      </Row>
                    </Column>
                  </InputField>
                )}
              />
            </InputGroup.Column>
          </InputGroup.Row>

          <Error errors={errors} />

          <Row modifiers="middle">
            <Column modifiers="fluid">
              <Text modifiers="small">
                <Trans>Call with:</Trans>
              </Text>
            </Column>
            <Column modifiers={['padScaleX_2', 'col']}>
              <Row>
                <Column>
                  <Checkbox
                    checked={values[contactFields.callbackEta]}
                    id={`eta-${formId}`}
                    label={<Trans>ETA</Trans>}
                    name={contactFields.callbackEta}
                    onChange={this.onEtaChange}
                    readOnly={isReadOnlyCase}
                  />
                </Column>
                <Column>
                  <Checkbox
                    checked={values[contactFields.callbackRollTime]}
                    id={`rollWith=${formId}`}
                    label={<Trans>Roll Time</Trans>}
                    name={contactFields.callbackRollTime}
                    onChange={this.onRollbackChange}
                    readOnly={isReadOnlyCase}
                  />
                </Column>
              </Row>
            </Column>
            <Column modifiers={['end', 'fluid', 'padScaleY_0']}>
              <ContactCallButton
                enableCallButton={enableCallButton}
                name={contactName}
                phone={phoneNumber}
              />
            </Column>
            {isReadOnlyCase || (
              <Column modifiers={['end', 'fluid', 'padScaleY_0']}>
                <ContactDeleteButton
                  deletable={deletable}
                  onDelete={onDelete}
                />
              </Column>
            )}
          </Row>
        </InputGroup>
      </Form>
    );
  }
}

const buildFocusReceiver = (fieldName, propName) =>
  useFocusReceiver(
    ({ index }) => fieldName.replace('{index}', index),
    propName,
  );

export default compose(
  setDisplayName('ContactDetailsForm'),
  withI18n(),
  withPatternValidationOnKeyUp({ fields: { name: invalidCharsForExporting } }),
  buildFocusReceiver(fieldIds.name, 'nameFocusHandler'),
  buildFocusReceiver(fieldIds.phone, 'phoneFocusHandler'),
  buildFocusReceiver(fieldIds.email, 'emailFocusHandler'),
)(ContactDetailsForm);



// WEBPACK FOOTER //
// ./src/compositions/CaseContacts/ContactDetails/ContactDetailsForm.js